import { useState, useCallback, useRef, useLayoutEffect, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { apiClient } from '../api/client'

interface City {
  id: number
  name: string
  slug: string
  regionName?: string
}

interface CitySelectProps {
  value: { id: number; name: string } | null
  onChange: (city: { id: number; name: string }) => void
  error?: string | null
}

export function CitySelect({ value, onChange, error }: CitySelectProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [cities, setCities] = useState<City[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const scrollFieldIntoView = useCallback(() => {
    const el = inputRef.current ?? rootRef.current
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'start', behavior: 'smooth', inline: 'nearest' })
    })
  }, [])

  /** Поднять поле над клавиатурой (мини-приложение MAX / мобильный браузер). */
  useLayoutEffect(() => {
    if (!isOpen) return
    const vv = typeof window !== 'undefined' ? window.visualViewport : null
    if (!vv) {
      scrollFieldIntoView()
      return
    }
    const bump = () => {
      const el = inputRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const overlap = rect.bottom - (vv.height + vv.offsetTop) + 12
      if (overlap > 0) {
        window.scrollBy({ top: overlap, behavior: 'smooth' })
      }
    }
    bump()
    vv.addEventListener('resize', bump)
    vv.addEventListener('scroll', bump)
    return () => {
      vv.removeEventListener('resize', bump)
      vv.removeEventListener('scroll', bump)
    }
  }, [isOpen, scrollFieldIntoView])

  useLayoutEffect(() => {
    if (isOpen) {
      scrollFieldIntoView()
    }
  }, [isOpen, scrollFieldIntoView])

  const searchCities = useCallback(async (q: string) => {
    if (q.length < 1) {
      setCities([])
      return
    }

    setIsLoading(true)
    try {
      const results = await apiClient.get<City[]>(`/api/reference/cities?q=${q}`)
      setCities(results)
    } catch {
      setCities([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value
    setQuery(q)
    setIsOpen(true)
    void searchCities(q)
  }

  const handleSelectCity = (city: City) => {
    onChange({ id: city.id, name: city.name })
    setQuery('')
    setIsOpen(false)
    setCities([])
  }

  const closeSheet = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSheet()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, closeSheet])

  const showSheet =
    isOpen && (isLoading || cities.length > 0 || (Boolean(query.trim()) && !isLoading))

  const sheet =
    typeof document !== 'undefined' && showSheet
      ? createPortal(
          <div className="fixed inset-0 z-[600] flex flex-col justify-end" role="presentation">
            <button
              type="button"
              aria-label="Закрыть список городов"
              className="absolute inset-0 bg-black/45"
              onClick={closeSheet}
            />
            <div
              className="relative z-[601] mx-auto mb-0 w-full max-w-lg rounded-t-2xl border border-gray-200 bg-white shadow-2xl dark:border-slate-600 dark:bg-slate-900"
              style={{ maxHeight: 'min(52dvh, 360px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            >
              <div className="border-b border-gray-100 px-3 py-2 text-center text-xs font-medium text-gray-500 dark:border-slate-700 dark:text-slate-400">
                Выберите город
              </div>
              <div className="max-h-[min(48dvh,320px)] overflow-y-auto overscroll-y-contain">
                {isLoading && (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-slate-400">Поиск…</div>
                )}
                {!isLoading && cities.length === 0 && query.trim() ? (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-slate-400">Города не найдены</div>
                ) : null}
                {cities.map((city) => (
                  <button
                    key={city.id}
                    type="button"
                    onClick={() => handleSelectCity(city)}
                    className="w-full border-b border-gray-50 px-4 py-3 text-left last:border-0 hover:bg-gray-50 dark:border-slate-800 dark:hover:bg-slate-800/80"
                  >
                    <div className="font-medium text-gray-900 dark:text-slate-100">{city.name}</div>
                    {city.regionName ? (
                      <div className="text-xs text-gray-500 dark:text-slate-400">{city.regionName}</div>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )
      : null

  return (
    <div ref={rootRef} className="relative scroll-mt-24">
      <input
        ref={inputRef}
        type="text"
        value={value?.name || query}
        onChange={handleInputChange}
        onFocus={() => {
          setIsOpen(true)
          scrollFieldIntoView()
        }}
        placeholder="Введи название города"
        autoComplete="off"
        className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-900/60 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 ${
          error
            ? 'border-red-500 focus:ring-red-300 dark:focus:ring-red-500/40'
            : 'border-gray-300 dark:border-gray-600 focus:ring-accent'
        }`}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      {sheet}
    </div>
  )
}
